import React, { useState } from 'react';
import { Bell, Calendar, BookOpen, FileText, Clock, Megaphone, CheckCheck } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

type NotifType = 'kehadiran' | 'akademik' | 'pekeliling' | 'laporan' | 'jadual' | 'am';

interface Notif {
  id:       string;
  type:     NotifType;
  titleMs:  string;
  titleEn:  string;
  ts:       string;
  read:     boolean;
}

const NOTIFS: Notif[] = [
  { id:'n1', type:'kehadiran', titleMs:'Ahmad tidak hadir ke sekolah hari ini', titleEn:'Ahmad was absent from school today', ts:'Hari ini, 8:15 pagi', read:false },
  { id:'n2', type:'akademik',  titleMs:'Keputusan Matematik telah dikemaskini — 91%', titleEn:'Mathematics result updated — 91%', ts:'Semalam, 3:22 ptg', read:false },
  { id:'n3', type:'pekeliling',titleMs:'Pekeliling baharu memerlukan pengesahan anda', titleEn:'New circular requires your acknowledgement', ts:'2 Jun 2025, 9:00 pagi', read:true },
  { id:'n4', type:'laporan',   titleMs:'Laporan Kemajuan Penggal 1 2025/2026 kini tersedia', titleEn:'Term 1 2025/2026 Progress Report is now available', ts:'15 Mac 2026, 8:00 pagi', read:true },
  { id:'n5', type:'jadual',    titleMs:'Jadual waktu telah dikemaskini pada 1 Jun 2025', titleEn:'Timetable was updated on 1 June 2025', ts:'1 Jun 2025, 7:30 pagi', read:true },
  { id:'n6', type:'am',        titleMs:'Peringatan: Sukan Tahunan pada 20 Jun 2025', titleEn:'Reminder: Annual Sports Day on 20 June 2025', ts:'31 Mei 2025, 2:00 ptg', read:true },
  { id:'n7', type:'kehadiran', titleMs:'Kadar kehadiran Ahmad minggu ini: 80%', titleEn:'Ahmad\'s attendance rate this week: 80%', ts:'30 Mei 2025, 4:00 ptg', read:true },
];

const TYPE_META: Record<NotifType, { icon: React.FC<any>; color: string; bg: string; labelMs: string; labelEn: string }> = {
  kehadiran: { icon: Calendar,    color: T.color.danger,   bg: T.color.dangerLight,   labelMs: 'Kehadiran', labelEn: 'Attendance'  },
  akademik:  { icon: BookOpen,    color: T.color.info,     bg: T.color.infoLight,     labelMs: 'Akademik',  labelEn: 'Academic'    },
  pekeliling:{ icon: Megaphone,   color: T.color.warning,  bg: T.color.warningLight,  labelMs: 'Pekeliling',labelEn: 'Circular'    },
  laporan:   { icon: FileText,    color: T.color.success,  bg: T.color.successLight,  labelMs: 'Laporan',   labelEn: 'Report'      },
  jadual:    { icon: Clock,       color: '#7C3AED',         bg: '#F5F3FF',             labelMs: 'Jadual',    labelEn: 'Timetable'   },
  am:        { icon: Bell,        color: '#4A5568',         bg: '#F3F4F6',             labelMs: 'Umum',      labelEn: 'General'     },
};

export function ParentPemberitahuanPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Notifications' : 'Pemberitahuan');

  const [notifs, setNotifs]   = useState<Notif[]>(NOTIFS);
  const [filter, setFilter]   = useState<'all' | NotifType>('all');

  const unread = notifs.filter(n => !n.read).length;

  function markAllRead() {
    setNotifs(ns => ns.map(n => ({ ...n, read: true })));
  }

  function markRead(id: string) {
    setNotifs(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
  }

  const shown = notifs.filter(n => filter === 'all' || n.type === filter);

  return (
    <div style={{ maxWidth: '760px' }}>
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Notifications' : 'Pemberitahuan'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {unread > 0
              ? (E ? `${unread} unread notification(s)` : `${unread} pemberitahuan belum dibaca`)
              : (E ? 'All notifications read' : 'Semua pemberitahuan telah dibaca')}
          </p>
        </div>
        {unread > 0 && (
          <button className="btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={markAllRead}>
            <CheckCheck size={14} />
            {E ? 'Mark all read' : 'Tandakan semua dibaca'}
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button className={`pill-filter${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>
          {E ? 'All' : 'Semua'}
        </button>
        {(Object.entries(TYPE_META) as [NotifType, typeof TYPE_META[NotifType]][]).map(([k, v]) => (
          <button key={k} className={`pill-filter${filter === k ? ' active' : ''}`} onClick={() => setFilter(k)}>
            {E ? v.labelEn : v.labelMs}
          </button>
        ))}
      </div>

      {/* Notification list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {shown.map(n => {
          const meta = TYPE_META[n.type];
          const IconComp = meta.icon;
          return (
            <div
              key={n.id}
              className="tbl-row"
              onClick={() => markRead(n.id)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '14px',
                padding: '16px 20px',
                background: n.read ? '#FFFFFF' : '#FFFDF4',
                border: `1px solid ${n.read ? '#E5E7EB' : '#F7D000'}`,
                borderRadius: '10px',
                cursor: 'pointer',
              }}
            >
              {/* Icon */}
              <div style={{ width: '36px', height: '36px', background: meta.bg, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconComp size={18} color={meta.color} />
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span className="badge" style={{ background: meta.bg, color: meta.color, fontSize: '10px' }}>
                    {E ? meta.labelEn : meta.labelMs}
                  </span>
                  {!n.read && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F7D000', flexShrink: 0 }} />}
                </div>
                <div style={{ fontSize: '13px', fontWeight: n.read ? 400 : 600, color: T.color.textPrimary, lineHeight: 1.5 }}>
                  {E ? n.titleEn : n.titleMs}
                </div>
                <div style={{ fontSize: '11px', color: T.color.textDisabled, marginTop: '4px' }}>{n.ts}</div>
              </div>
            </div>
          );
        })}

        {shown.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: T.color.textSecondary, fontSize: '13px' }}>
            {E ? 'No notifications for this filter.' : 'Tiada pemberitahuan untuk penapis ini.'}
          </div>
        )}
      </div>
    </div>
  );
}
